/**
 * SyncWords caption flows (`flow.engine === 'syncwords'`). The encoder pushes
 * SRT to SyncWords' ingest URL (`flow.ingest`); SyncWords adds DVB-TXT
 * (Teletext) subtitle pages, one per language (the spoken language plus its
 * translations), and pushes the video into the linked Castr stream, so its
 * player and restream destinations carry them. Teletext shows Latin,
 * Cyrillic, Greek, Arabic and Hebrew scripts only.
 *
 * Contract (core):
 *   GET  /captions/engine -> { engine, syncwords: { configured, transcriptionTypes,
 *        spokenLanguages: { Speechmatics: [{ code, name }] }, translationLanguages: [{ code, name }],
 *        translationEngines, regions, captionFormat: 'dvb-txt', textColors, alignments,
 *        ranges: { lineCount, maxLineLength, bufferSec }, defaults, disconnectStopMin } }
 *        Language entries carry `teletext: false` when Teletext can't show their script.
 *   POST /captions { streamKey, syncwords? }, PUT /captions/:id { syncwords },
 *   POST /captions/:id/start { durationSec, syncwords? }
 *   `syncwords` settings: { transcriptionType, spokenLanguage, translations: [{ language, engine }],
 *        position, lineCount, maxLineLength, alignment, doubleHeight, textColor,
 *        region, bufferSec, diarization, filterDisfluency, ingestPassphrase }
 *   flow.syncwords also carries `pages: [{ language, page }]` (spoken 888, translations 889...)
 *   and `captionSupport: { enabled, managed }`: creating the flow turns the stream's
 *   "Enable Caption Support" player setting on (`managed` when captioning did it).
 *   400 captions_unsupported_caption_language for an output language Teletext can't show.
 *   flow.langs = [spokenLanguage, ...translation languages]; the plan's `limits.maxLangs`
 *   counts all of them (409 captions_too_many_languages beyond it).
 *   Status adds `engineState` (starting | live | waiting_for_input | stopped | error | unknown)
 *   and `syncwords: { status, isInputConnected, inputDisconnectedSince, disconnectStopAt }`.
 *   The list `summary` carries `engineState` from the last status core saw.
 *   `GET /captions/engine?streamKey=` adds `stream: { key, castrRegion, region, regionSource }`
 *   (`region` = the one nearest that stream, also in `defaults.region`).
 *
 * Bring your own account: `GET|PUT|DELETE /captions/provider` connects the
 * customer's SyncWords keys (see `normalizeProvider`). New flows then run on
 * that account (`flow.syncwords.keySource: 'account'`, `providerAccount`),
 * skip Castr's plan limits (`limits.billedBy: 'provider'`) and fail with
 * `captions_provider_disconnected | _mismatch | _rejected` when the account
 * is gone, swapped or refuses. `GET /captions/engine` adds `provider`.
 * SyncWords has no quota API, only usage: the provider and own-account flow
 * responses carry `providerUsage` (this calendar month, UTC) or null.
 *
 * Castr flows (`engine` 'castr' or missing) never reach this module.
 */
import { languageName, isUnreachableStatus, windowOf, offLabel, formatClock, toMs, joinLanguageNames } from './captionLanguages';
import { formatHours } from './captionLimits';

export const ENGINE_CASTR = 'castr';
export const ENGINE_SYNCWORDS = 'syncwords';

export const isSyncWordsFlow = (flow) => flow?.engine === ENGINE_SYNCWORDS;

export const ENGINE_STATES = Object.freeze(['starting', 'live', 'waiting_for_input', 'stopped', 'error', 'unknown']);

export const PASSPHRASE_RE = /^[a-zA-Z0-9]{10,79}$/;
export const PASSPHRASE_RULE = '10 to 79 letters and digits.';

export const REGION_NAMES = Object.freeze({
	'us-east-1': 'US East (N. Virginia)',
	'us-west-2': 'US West (Oregon)',
	'ca-central-1': 'Canada (Central)',
	'ap-southeast-2': 'Asia Pacific (Sydney)',
	'eu-central-1': 'Europe (Frankfurt)',
	'eu-west-1': 'Europe (Ireland)',
});

export const BUFFER_HINT = 'Higher lets translations keep up; lower means less delay.';
export const AUTO_REGION_LABEL = 'Automatic (nearest to your stream)';
export const PLAYER_NOTE = 'Captions arrive as Teletext subtitle tracks (DVB-TXT) in the stream; pick a language with the player\'s CC button.';
export const TELETEXT_UNSUPPORTED = 'Not supported in Teletext subtitles';
export const TEXT_COLORS = Object.freeze(['white', 'yellow', 'cyan', 'green', 'magenta', 'red', 'blue']);
export const ALIGNMENTS = Object.freeze(['left', 'center']);

/** "Europe · Frankfurt" from "Europe (Frankfurt)"; the code itself when unnamed. */
export function regionShortName(code) {
	const name = REGION_NAMES[code];
	if (!name) return code || '';
	const m = /^(.*) \((.*)\)$/.exec(name);
	return m ? `${m[1]} · ${m[2]}` : name;
}

/** The automatic choice, naming the region core would pick when it is known. */
export const autoRegionLabel = (code) => (code ? `Automatic (${regionShortName(code)})` : AUTO_REGION_LABEL);

/**
 * What a settings form offers when core can't be asked (older core, or the
 * request failed): broadcast-standard defaults (bottom, 2 double-height white
 * centred lines, speaker labels and filler-word removal on, 30 s buffer,
 * Speechmatics, no translations) and a short language list. Core's
 * `defaults` win field by field when it sends them.
 */
const FALLBACK_LANGS = [
	['en', 'English'], ['es', 'Spanish'], ['fr', 'French'], ['de', 'German'], ['it', 'Italian'],
	['pt', 'Portuguese'], ['nl', 'Dutch'], ['pl', 'Polish'], ['ru', 'Russian'], ['ar', 'Arabic'],
].map(([code, name]) => ({ code, name, teletext: true }));

export const FALLBACK_ENGINE_INFO = Object.freeze({
	engine: ENGINE_CASTR,
	syncwords: Object.freeze({
		configured: false,
		transcriptionTypes: ['Speechmatics', 'Soniox', 'Sarvam'],
		spokenLanguages: { Speechmatics: FALLBACK_LANGS },
		translationLanguages: FALLBACK_LANGS,
		translationEngines: ['Google', 'DeepL', 'Gemini'],
		regions: Object.keys(REGION_NAMES),
		captionFormat: 'dvb-txt',
		textColors: TEXT_COLORS,
		alignments: ALIGNMENTS,
		ranges: { lineCount: { min: 1, max: 2 }, maxLineLength: { min: 20, max: 32 }, bufferSec: { min: 5, max: 60 } },
		defaults: {
			transcriptionType: 'Speechmatics',
			spokenLanguage: 'en',
			translations: [],
			region: null,
			position: 'bottom',
			lineCount: 2,
			maxLineLength: 32,
			alignment: 'center',
			doubleHeight: true,
			textColor: 'white',
			bufferSec: 30,
			diarization: true,
			filterDisfluency: true,
			ingestPassphrase: null,
		},
		disconnectStopMin: 10,
	}),
});

// `teletext: false` marks a language whose script Teletext can't show; absent means it can.
const named = (list) => (Array.isArray(list) ? list : [])
	.map((l) => (typeof l === 'string' ? { code: l, name: languageName(l) } : l))
	.filter((l) => l && typeof l.code === 'string' && l.code)
	.map((l) => ({ code: l.code, name: typeof l.name === 'string' && l.name ? l.name : languageName(l.code), teletext: l.teletext !== false }));

const range = (raw, fallback) => {
	const min = Number(raw?.min);
	const max = Number(raw?.max);
	return Number.isInteger(min) && Number.isInteger(max) && min <= max ? { min, max } : { ...fallback };
};

/** `GET /captions/engine` with every list and range filled in. */
export function normalizeEngineInfo(raw) {
	const fb = FALLBACK_ENGINE_INFO.syncwords;
	const sw = raw?.syncwords && typeof raw.syncwords === 'object' ? raw.syncwords : {};
	const spoken = {};
	const rawSpoken = sw.spokenLanguages && typeof sw.spokenLanguages === 'object' ? sw.spokenLanguages : {};
	for (const [type, list] of Object.entries(rawSpoken)) spoken[type] = named(list);
	if (!Object.keys(spoken).length) Object.assign(spoken, fb.spokenLanguages);
	const translationLanguages = named(sw.translationLanguages);
	const list = (value, fallback) => (Array.isArray(value) && value.length ? value.filter((v) => typeof v === 'string') : [...fallback]);
	const ranges = sw.ranges && typeof sw.ranges === 'object' ? sw.ranges : {};
	const rawDefaults = sw.defaults && typeof sw.defaults === 'object' ? sw.defaults : {};
	// A field core leaves out (or sends as null) keeps the broadcast default.
	const defaults = { ...fb.defaults };
	for (const [field, value] of Object.entries(rawDefaults)) {
		if (value !== null && value !== undefined) defaults[field] = value;
	}
	defaults.translations = normalizeTranslations(defaults.translations);
	const stopMin = Number(sw.disconnectStopMin);
	const stream = raw?.stream && typeof raw.stream === 'object' && typeof raw.stream.region === 'string' && raw.stream.region
		? {
			key: typeof raw.stream.key === 'string' ? raw.stream.key : null,
			castrRegion: typeof raw.stream.castrRegion === 'string' ? raw.stream.castrRegion : null,
			region: raw.stream.region,
			regionSource: raw.stream.regionSource === 'stream' ? 'stream' : 'default',
		}
		: null;
	const provider = raw?.provider && typeof raw.provider === 'object' ? raw.provider : {};
	return {
		engine: raw?.engine === ENGINE_SYNCWORDS ? ENGINE_SYNCWORDS : ENGINE_CASTR,
		stream,
		provider: { connected: provider.connected === true, account: providerAccount(provider.account) },
		syncwords: {
			configured: sw.configured !== false && !!raw?.syncwords,
			// Core sent its translation list: the form may refuse codes outside it.
			listsKnown: translationLanguages.length > 0,
			transcriptionTypes: list(sw.transcriptionTypes, fb.transcriptionTypes),
			spokenLanguages: spoken,
			translationLanguages: translationLanguages.length ? translationLanguages : [...fb.translationLanguages],
			translationEngines: list(sw.translationEngines, fb.translationEngines),
			regions: list(sw.regions, fb.regions),
			captionFormat: typeof sw.captionFormat === 'string' ? sw.captionFormat : fb.captionFormat,
			textColors: list(sw.textColors, fb.textColors),
			alignments: list(sw.alignments, fb.alignments),
			ranges: {
				lineCount: range(ranges.lineCount, fb.ranges.lineCount),
				maxLineLength: range(ranges.maxLineLength, fb.ranges.maxLineLength),
				bufferSec: range(ranges.bufferSec, fb.ranges.bufferSec),
			},
			defaults,
			disconnectStopMin: Number.isFinite(stopMin) && stopMin > 0 ? stopMin : fb.disconnectStopMin,
		},
	};
}

const numOrNull = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const msOrNull = (v) => {
	const ms = toMs(v);
	return Number.isFinite(ms) ? ms : null;
};

/** `[{ language, engine }]` with blank entries dropped; engine null means automatic. */
export function normalizeTranslations(raw) {
	return (Array.isArray(raw) ? raw : [])
		.filter((t) => t && typeof t.language === 'string' && t.language)
		.map((t) => ({ language: t.language, engine: typeof t.engine === 'string' && t.engine ? t.engine : null }));
}

/** `flow.ingest` for a syncwords flow: `{ protocol, url, passphrase }`. */
export function normalizeIngest(raw) {
	const ingest = raw && typeof raw === 'object' ? raw : {};
	return {
		protocol: typeof ingest.protocol === 'string' ? ingest.protocol : 'srt',
		url: typeof ingest.url === 'string' && ingest.url ? ingest.url : null,
		passphrase: typeof ingest.passphrase === 'string' && ingest.passphrase ? ingest.passphrase : null,
	};
}

const clampInt = (v, min, max, fallback) => {
	const n = Math.round(Number(v));
	return Number.isFinite(n) && v !== null && v !== '' ? Math.min(max, Math.max(min, n)) : fallback;
};

/**
 * `flow.syncwords`, with numbers and booleans coerced. A flow stored before
 * translations (one `captionLanguage`) reads as that one translation.
 */
export function normalizeSyncWordsBlock(raw) {
	const sw = raw && typeof raw === 'object' ? raw : {};
	const str = (v) => (typeof v === 'string' && v ? v : null);
	let translations = normalizeTranslations(sw.translations);
	if (!Array.isArray(sw.translations) && str(sw.captionLanguage) && !sameLanguage(sw.captionLanguage, sw.spokenLanguage)) {
		translations = [{ language: sw.captionLanguage, engine: str(sw.translationEngine) }];
	}
	return {
		serviceId: numOrNull(sw.serviceId),
		region: str(sw.region),
		transcriptionType: str(sw.transcriptionType),
		spokenLanguage: str(sw.spokenLanguage),
		translations,
		position: sw.position === 'top' ? 'top' : 'bottom',
		lineCount: clampInt(sw.lineCount, 1, 2, 2),
		maxLineLength: clampInt(sw.maxLineLength, 20, 32, 32),
		alignment: sw.alignment === 'left' ? 'left' : 'center',
		doubleHeight: sw.doubleHeight !== false,
		textColor: TEXT_COLORS.includes(sw.textColor) ? sw.textColor : 'white',
		captionSupport: sw.captionSupport && typeof sw.captionSupport === 'object'
			? { enabled: sw.captionSupport.enabled === true, managed: sw.captionSupport.managed === true }
			: null,
		pages: (Array.isArray(sw.pages) ? sw.pages : [])
			.filter((p) => p && typeof p.language === 'string' && Number.isFinite(Number(p.page)))
			.map((p) => ({ language: p.language, page: Number(p.page) })),
		bufferSec: numOrNull(sw.bufferSec),
		diarization: sw.diarization === true,
		filterDisfluency: sw.filterDisfluency !== false,
		captionFormat: str(sw.captionFormat) || 'dvb-txt',
		keySource: sw.keySource === 'account' ? 'account' : 'castr',
		providerAccount: providerAccount(sw.providerAccount),
		lastStatus: str(sw.lastStatus),
		inputConnected: typeof sw.inputConnected === 'boolean' ? sw.inputConnected : null,
		inputDisconnectedSince: msOrNull(sw.inputDisconnectedSince),
		statusCheckedAt: msOrNull(sw.statusCheckedAt),
		disconnectStopMin: numOrNull(sw.disconnectStopMin),
	};
}

/** Folds the syncwords status fields onto a status payload (castr statuses pass through). */
export function normalizeSyncWordsStatus(status) {
	if (!status || typeof status !== 'object' || status.engine !== ENGINE_SYNCWORDS) return status;
	const sw = status.syncwords && typeof status.syncwords === 'object' ? status.syncwords : {};
	const provider = status.provider && typeof status.provider === 'object' ? status.provider : null;
	return {
		...status,
		provider: provider ? { keySource: provider.keySource === 'account' ? 'account' : 'castr', connected: provider.connected !== false } : null,
		engineState: ENGINE_STATES.includes(status.engineState) ? status.engineState : 'unknown',
		syncwords: {
			status: typeof sw.status === 'string' ? sw.status : null,
			isInputConnected: typeof sw.isInputConnected === 'boolean' ? sw.isInputConnected : null,
			inputDisconnectedSince: msOrNull(sw.inputDisconnectedSince),
			disconnectStopAt: msOrNull(sw.disconnectStopAt),
		},
	};
}

export const sameLanguage = (a, b) => !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

/** Name of a SyncWords language code: core's lists first, then the shared lookup. */
export function syncwordsLanguageName(code, info = null) {
	if (!code) return '';
	const fold = String(code).toLowerCase();
	const sw = info?.syncwords;
	if (sw) {
		const lists = [sw.translationLanguages, ...Object.values(sw.spokenLanguages || {})];
		for (const list of lists) {
			const hit = (list || []).find((l) => l.code.toLowerCase() === fold);
			if (hit) return hit.name;
		}
	}
	return languageName(code);
}

/** Spoken-language options for a speech engine; engines without a list reuse Speechmatics'. */
export function spokenOptions(info, transcriptionType) {
	const lists = info?.syncwords?.spokenLanguages || {};
	return lists[transcriptionType] || lists.Speechmatics || Object.values(lists)[0] || [];
}

/** `options` plus the current code when the list lacks it, so a saved value always shows by name. */
export function withCurrent(options, code, info = null) {
	if (!code || options.some((o) => o.code.toLowerCase() === String(code).toLowerCase())) return options;
	return [{ code, name: syncwordsLanguageName(code, info) }, ...options];
}

export const isTranslationLanguage = (code, info) =>
	!!code && (info?.syncwords?.translationLanguages || []).some((l) => l.code.toLowerCase() === String(code).toLowerCase());

/** False when core's lists mark the language as a script Teletext can't show. */
export function isTeletextLanguage(code, info) {
	if (!code) return true;
	const fold = String(code).toLowerCase();
	const sw = info?.syncwords;
	const lists = [sw?.translationLanguages, ...Object.values(sw?.spokenLanguages || {})];
	for (const list of lists) {
		const hit = (list || []).find((l) => l.code.toLowerCase() === fold);
		if (hit && hit.teletext === false) return false;
	}
	return true;
}

/** Picker options with the unsupported-script languages disabled and labelled. */
export const withTeletextReasons = (options) =>
	options.map((o) => (o.teletext === false ? { ...o, disabledReason: TELETEXT_UNSUPPORTED } : o));

/** The Teletext page of one of a flow's languages (`flow.syncwords.pages`), or null. */
export function teletextPage(flow, code) {
	const hit = (flow?.syncwords?.pages || []).find((p) => sameLanguage(p.language, code));
	return hit ? hit.page : null;
}

/** A flow's caption languages: the spoken one first, then its translations. */
export function flowLanguages(flow) {
	if (Array.isArray(flow?.langs) && flow.langs.length) return flow.langs;
	const sw = flow?.syncwords;
	return sw?.spokenLanguage ? [sw.spokenLanguage, ...(sw.translations || []).map((t) => t.language)] : [];
}

/** "English + 2 translations", "English only" for the list card. */
export function languagesSummary(flow, info = null) {
	const [spoken, ...rest] = flowLanguages(flow);
	if (!spoken) return '';
	const name = syncwordsLanguageName(spoken, info);
	if (!rest.length) return `${name} only`;
	return `${name} + ${rest.length} ${rest.length === 1 ? 'translation' : 'translations'}`;
}

/** "English, French and Japanese" for tooltips. */
export const languagesSentence = (flow) => joinLanguageNames(flowLanguages(flow));

// ---------------------------------------------------------------- Position

/** Teletext position: core derives the starting row (bottom: 24 - lines x height, top: 2). */
export const POSITION_OPTIONS = Object.freeze([
	{ value: 'bottom', label: 'Bottom' },
	{ value: 'top', label: 'Top' },
]);
export const ALIGNMENT_OPTIONS = Object.freeze([
	{ value: 'left', label: 'Left' },
	{ value: 'center', label: 'Center' },
]);
export const TEXT_COLOR_SWATCHES = Object.freeze({
	white: '#ffffff', yellow: '#ffff00', cyan: '#00ffff', green: '#00ff00', magenta: '#ff00ff', red: '#ff0000', blue: '#0000ff',
});

/**
 * Region choices; `automatic` adds the empty choice that lets core pick the
 * nearest region, named after `autoRegion` (the region it would pick) when known.
 */
export function regionOptions(info, { automatic = false, autoRegion = null } = {}) {
	const regions = (info?.syncwords?.regions || []).map((code) => ({ value: code, text: REGION_NAMES[code] ? `${REGION_NAMES[code]} · ${code}` : code }));
	return automatic ? [{ value: '', text: autoRegionLabel(autoRegion) }, ...regions] : regions;
}

// ------------------------------------------------------------------ Drafts

/**
 * The settings form draft for a flow, or for a new flow when `flow` is null:
 * core's defaults (broadcast-standard fallbacks) and no region, so core
 * picks the one nearest the stream.
 */
export function draftFromFlow(flow, info) {
	const d = info?.syncwords?.defaults || FALLBACK_ENGINE_INFO.syncwords.defaults;
	const sw = flow?.syncwords || {};
	const spoken = sw.spokenLanguage || flow?.source || d.spokenLanguage || 'en';
	const pick = (v, fallback) => (v === null || v === undefined ? fallback : v);
	const from = flow ? sw : d;
	return {
		transcriptionType: sw.transcriptionType || d.transcriptionType || 'Speechmatics',
		spokenLanguage: spoken,
		translations: normalizeTranslations(flow ? sw.translations : d.translations).filter((t) => !sameLanguage(t.language, spoken)),
		region: flow ? sw.region || '' : '',
		position: from.position === 'top' ? 'top' : 'bottom',
		lineCount: Number(pick(from.lineCount, d.lineCount)),
		maxLineLength: Number(pick(from.maxLineLength, d.maxLineLength)),
		alignment: from.alignment === 'left' ? 'left' : 'center',
		doubleHeight: from.doubleHeight !== false,
		textColor: from.textColor || d.textColor || 'white',
		bufferSec: Number(pick(sw.bufferSec, d.bufferSec)),
		diarization: flow ? sw.diarization === true : d.diarization !== false,
		filterDisfluency: flow ? sw.filterDisfluency !== false : d.filterDisfluency !== false,
		ingestPassphrase: (flow ? flow.ingest?.passphrase : d.ingestPassphrase) || '',
	};
}

/** The `syncwords` settings object a draft sends. */
export function settingsFromDraft(draft) {
	const passphrase = String(draft.ingestPassphrase || '').trim();
	const out = {
		transcriptionType: draft.transcriptionType,
		spokenLanguage: draft.spokenLanguage,
		translations: normalizeTranslations(draft.translations),
		position: draft.position,
		lineCount: Number(draft.lineCount),
		maxLineLength: draft.maxLineLength === '' ? '' : Number(draft.maxLineLength),
		alignment: draft.alignment,
		doubleHeight: !!draft.doubleHeight,
		textColor: draft.textColor,
		bufferSec: Number(draft.bufferSec),
		diarization: !!draft.diarization,
		filterDisfluency: !!draft.filterDisfluency,
		ingestPassphrase: passphrase || null,
	};
	if (draft.region) out.region = draft.region;
	return out;
}

export const draftKey = (draft) => JSON.stringify(settingsFromDraft(draft));

/**
 * Field errors the form shows before sending: `{ valid, errors: { [field]: message } }`.
 * `maxLangs` is the plan's cap on languages per flow, the spoken one included.
 */
export function validateDraft(draft, info, { maxLangs = Infinity } = {}) {
	const errors = {};
	const ranges = info?.syncwords?.ranges || FALLBACK_ENGINE_INFO.syncwords.ranges;
	const inRange = (v, r) => Number.isInteger(Number(v)) && v !== '' && v !== null && Number(v) >= r.min && Number(v) <= r.max;
	if (!draft.spokenLanguage) errors.spokenLanguage = 'Choose the spoken language.';
	else if (!isTeletextLanguage(draft.spokenLanguage, info)) {
		errors.spokenLanguage = `${syncwordsLanguageName(draft.spokenLanguage, info)} isn't supported in Teletext subtitles; the spoken language is a subtitle track too.`;
	}
	const translations = normalizeTranslations(draft.translations);
	const seen = new Set([String(draft.spokenLanguage || '').toLowerCase()]);
	for (const t of translations) {
		const fold = t.language.toLowerCase();
		if (seen.has(fold)) {
			errors.translations = `${syncwordsLanguageName(t.language, info)} is already a caption language.`;
			break;
		}
		seen.add(fold);
		if (info?.syncwords?.listsKnown && !isTranslationLanguage(t.language, info)) {
			errors.translations = `${syncwordsLanguageName(t.language, info)} can't be translated to.`;
			break;
		}
		if (!isTeletextLanguage(t.language, info)) {
			errors.translations = `${syncwordsLanguageName(t.language, info)} isn't supported in Teletext subtitles.`;
			break;
		}
	}
	if (!errors.translations && 1 + translations.length > maxLangs) {
		errors.translations = `Your plan includes ${maxLangs} ${maxLangs === 1 ? 'language' : 'languages'} per flow. Remove a language to save.`;
	}
	if (!POSITION_OPTIONS.some((o) => o.value === draft.position)) errors.position = 'Choose where captions appear.';
	if (!inRange(draft.lineCount, ranges.lineCount)) errors.lineCount = `Choose ${ranges.lineCount.min} or ${ranges.lineCount.max} lines.`;
	if (!inRange(draft.maxLineLength, ranges.maxLineLength)) {
		errors.maxLineLength = `Use a whole number from ${ranges.maxLineLength.min} to ${ranges.maxLineLength.max}.`;
	}
	if (!ALIGNMENT_OPTIONS.some((o) => o.value === draft.alignment)) errors.alignment = 'Choose an alignment.';
	if (!(info?.syncwords?.textColors || TEXT_COLORS).includes(draft.textColor)) errors.textColor = 'Choose a text colour.';
	if (!inRange(draft.bufferSec, ranges.bufferSec)) errors.bufferSec = `Use a whole number from ${ranges.bufferSec.min} to ${ranges.bufferSec.max} seconds.`;
	const passphrase = String(draft.ingestPassphrase || '').trim();
	if (passphrase && !PASSPHRASE_RE.test(passphrase)) errors.ingestPassphrase = `Use ${PASSPHRASE_RULE}`;
	return { valid: Object.keys(errors).length === 0, errors };
}

const FIELDS = ['transcriptionType', 'spokenLanguage', 'translations', 'region', 'position', 'lineCount', 'maxLineLength', 'alignment', 'doubleHeight', 'textColor', 'bufferSec', 'diarization', 'filterDisfluency', 'ingestPassphrase'];

/**
 * Core's field errors (`syncwords.lineCount` from route validation,
 * `translations.1.language` from the settings validator, `/config/...` when
 * SyncWords refuses) as `{ fields: { [field]: message }, general: [message] }`.
 */
export function mapServerErrors(errors) {
	const fields = {};
	const general = [];
	for (const e of Array.isArray(errors) ? errors : []) {
		const message = typeof e?.message === 'string' ? e.message : '';
		const path = String(e?.path || '').replace(/^syncwords\./, '');
		const field = FIELDS.find((f) => path === f || path.startsWith(`${f}.`) || path.startsWith(`${f}[`));
		if (field && !fields[field]) fields[field] = message ? message.charAt(0).toUpperCase() + message.slice(1) : 'Invalid value.';
		else if (message) general.push(path && !field ? `${path}: ${message}` : message);
	}
	return { fields, general };
}

/**
 * A 400 captions_unsupported_caption_language as field errors: on the field
 * its `errors[].path` names, else on the spoken language or the translations
 * by the `language` it names. Core's message names the language and the scripts.
 */
export function unsupportedLanguageErrors(err, draft) {
	if (err?.code !== 'captions_unsupported_caption_language') return null;
	const { fields } = mapServerErrors(err.errors);
	const message = err.message || 'This language isn\'t supported in Teletext subtitles.';
	if (fields.spokenLanguage) return { spokenLanguage: message };
	if (fields.translations) return { translations: message };
	const language = err.data?.language || err.language;
	return sameLanguage(language, draft?.spokenLanguage) ? { spokenLanguage: message } : { translations: message };
}

// ------------------------------------------------------------------ Status

const STATE_COPY = {
	starting: { key: 'starting', label: 'Starting', tone: 'warning' },
	live: { key: 'live', label: 'Live', tone: 'success' },
	waiting_for_input: { key: 'waiting-input', label: 'Waiting for input', tone: 'warning' },
	error: { key: 'error', label: 'Error', tone: 'error' },
};

/**
 * One headline state for a syncwords flow, from a full status (manage page)
 * or the list `summary.engineState`. Keys: stream-removed, unknown, off,
 * starting, waiting-input, live, error. `tooltip` names the languages.
 */
export function syncwordsHeadline(flow, status = null) {
	if (flow?.streamDeleted) return { key: 'stream-removed', label: 'Linked livestream removed', tone: 'error' };
	if (status && isUnreachableStatus(status)) return { key: 'unknown', label: 'Unknown', tone: 'warning' };
	let engineState;
	let win;
	if (status) {
		engineState = status.engineState;
		win = windowOf(status, flow);
	} else if (flow && flow.summary !== undefined) {
		if (!flow.summary) return { key: 'unknown', label: 'Unknown', tone: 'warning' };
		engineState = flow.summary.engineState;
		win = windowOf(null, flow);
	} else {
		return { key: 'unknown', label: 'Checking', tone: 'muted' };
	}
	const names = languagesSentence(flow);
	const captionsIn = names ? `Captions in ${names}.` : '';
	if (STATE_COPY[engineState]) return { ...STATE_COPY[engineState], tooltip: captionsIn };
	if (engineState === 'unknown' && win.state === 'running') return { key: 'unknown', label: 'Unknown', tone: 'warning', tooltip: captionsIn };
	return { key: 'off', label: 'Off', tone: 'muted', tooltip: [win.state === 'idle' ? '' : offLabel(win), captionsIn].filter(Boolean).join(' · ') };
}

/** Copy for a window that ended, from its `endedReason`. */
export function syncwordsEndedCopy(win) {
	switch (win?.endedReason) {
		case 'input_disconnected':
			return 'Captions stopped because no encoder input arrived for a while.';
		case 'pool_exhausted':
			return 'Captions stopped because your plan\'s captioning hours for this cycle are used up.';
		case 'expired':
			return 'The session ended: its time limit was reached.';
		case 'stopped':
			return 'Captions were stopped.';
		default:
			return '';
	}
}

/** "Stops at 14:32 if no input arrives" for a waiting service, or ''. */
export function autoStopLabel(status, disconnectStopMin) {
	const at = status?.syncwords?.disconnectStopAt;
	if (Number.isFinite(at)) return `Stops automatically at ${formatClock(at)} if no input arrives.`;
	if (Number.isFinite(disconnectStopMin)) return `Stops automatically after ${disconnectStopMin} min without input.`;
	return '';
}

// --------------------------------------------------------- Own account

export const PROVIDER_ROUTE = '/manage/captioning';
export const PROVIDER_BANNER_DEFAULT = 'Using Syncwords? · Connect your own account';

const accountId = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : null);

/** `{ id, name }` of a connected SyncWords account, or null. */
export function providerAccount(raw) {
	if (!raw || typeof raw !== 'object') return null;
	const name = typeof raw.name === 'string' && raw.name ? raw.name : null;
	const id = accountId(raw.id);
	return name || id ? { id, name: name || `Account ${id}` } : null;
}

/**
 * `GET|PUT|DELETE /captions/provider` -> { provider, connected, account: { name, id,
 * isTrialAccount, regions: { default, available } } | null, connectedAt,
 * connectedBy: { id, name, email } | null, lastCheckedAt, lastError }. Keys never come back.
 */
export function normalizeProvider(raw) {
	const r = raw && typeof raw === 'object' ? raw : {};
	const account = providerAccount(r.account);
	const regions = r.account?.regions && typeof r.account.regions === 'object' ? r.account.regions : {};
	const by = r.connectedBy && typeof r.connectedBy === 'object' ? r.connectedBy : null;
	return {
		provider: typeof r.provider === 'string' ? r.provider : 'syncwords',
		connected: r.connected === true && !!account,
		account: account
			? {
				...account,
				isTrialAccount: r.account.isTrialAccount === true,
				regions: {
					default: typeof regions.default === 'string' ? regions.default : null,
					available: Array.isArray(regions.available) ? regions.available.filter((x) => typeof x === 'string') : [],
				},
			}
			: null,
		connectedAt: msOrNull(r.connectedAt),
		connectedBy: by ? { id: accountId(by.id), name: typeof by.name === 'string' ? by.name : '', email: typeof by.email === 'string' ? by.email : '' } : null,
		lastCheckedAt: msOrNull(r.lastCheckedAt),
		lastError: typeof r.lastError === 'string' && r.lastError ? r.lastError : null,
		providerUsage: normalizeProviderUsage(r.providerUsage),
	};
}

/**
 * `providerUsage: { periodStart, periodEnd, minutesCaptioned, minutesTranslated,
 * runtimeMinutes, checkedAt }` for the month so far, or null (core couldn't ask).
 */
export function normalizeProviderUsage(raw) {
	if (!raw || typeof raw !== 'object') return null;
	const minutes = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Math.max(0, Number(v)) : null);
	const minutesCaptioned = minutes(raw.minutesCaptioned);
	if (minutesCaptioned === null) return null;
	return {
		periodStart: msOrNull(raw.periodStart),
		periodEnd: msOrNull(raw.periodEnd),
		minutesCaptioned,
		minutesTranslated: minutes(raw.minutesTranslated),
		runtimeMinutes: minutes(raw.runtimeMinutes),
		checkedAt: msOrNull(raw.checkedAt),
	};
}

/** "12h 30m captioned this month on your SyncWords account", or '' without usage. */
export function providerUsageLabel(usage) {
	if (!usage || !Number.isFinite(usage.minutesCaptioned)) return '';
	return `${formatHours(usage.minutesCaptioned * 60)} captioned this month on your SyncWords account`;
}

/**
 * The list response's top-level `provider: { connected, account, usage }`
 * (usage = `providerUsage`), or null when core sent none.
 */
export function normalizeListProvider(raw) {
	if (!raw || typeof raw !== 'object') return null;
	const account = providerAccount(raw.account);
	return { connected: raw.connected === true && !!account, account, usage: normalizeProviderUsage(raw.usage) };
}

/** The banner on the captioning pages, from the engine's `provider` block. */
export function providerBannerText(provider) {
	if (provider?.connected && provider.account) return `Using your SyncWords account · ${provider.account.name}`;
	return PROVIDER_BANNER_DEFAULT;
}

export const isOwnAccountFlow = (flow) => flow?.syncwords?.keySource === 'account';

/** Flow-level own-account failures; each gets a Reconnect link to the settings. */
export const PROVIDER_FLOW_CODES = Object.freeze(['captions_provider_disconnected', 'captions_provider_mismatch', 'captions_provider_rejected']);

/** What an own-account flow's status says about the connection: '' | 'disconnected' | 'mismatch'. */
export function providerProblem(status) {
	if (!status || typeof status !== 'object') return '';
	if (status.error === 'provider_mismatch') return 'mismatch';
	if (status.error === 'provider_disconnected' || (status.provider?.keySource === 'account' && status.provider.connected === false)) return 'disconnected';
	return '';
}

// ------------------------------------------------------------------ Errors

/** Publisher copy for the syncwords error codes. */
export const SYNCWORDS_ERROR_MESSAGES = Object.freeze({
	captions_stream_pull_source: 'This stream pulls its video from a source URL, so it can\'t receive captioned video. Switch the stream to an encoder (push) source to use captions.',
	captions_srt_passphrase_invalid: 'This stream has SRT encryption on, but its key can\'t be used for captions (it must be 10 to 79 letters and digits). Set a new SRT encryption key on the stream, or turn SRT encryption off.',
	captions_settings_locked: 'Settings can\'t be changed while captions are running. Stop captions first.',
	captions_engine_not_configured: 'Captioning isn\'t available right now. Try again later or contact support.',
	captions_service_missing: 'The captioning service for this flow is missing. Delete this flow and create a new one, or contact support.',
	captions_export_pending: 'These captions are still being prepared. Try again in a few seconds.',
	captions_engine_unsupported: 'This isn\'t available for this captioning flow.',
	captions_already_running: 'Captions are already running.',
	captions_engine_busy: 'Captions are starting or stopping. Wait a moment and try again.',
	captions_provider_disconnected: 'This captioning runs on your SyncWords account, which is no longer connected. Reconnect it to start captions.',
	captions_provider_mismatch: 'This captioning was created on a different SyncWords account than the one connected now. Reconnect that account, or delete this captioning and create it again.',
	captions_provider_rejected: 'Your SyncWords account rejected the request. Check the account and its API keys.',
	captions_provider_invalid: 'Enter both the public and the private key (10 to 256 characters each).',
	captions_provider_unreachable: 'SyncWords couldn\'t be reached to check these keys. Try again in a moment.',
	captions_provider_unavailable: 'Connecting your own account isn\'t available right now. Try again later or contact support.',
	captions_provider_in_use: 'Stop captions on these streams before disconnecting the account.',
	captions_unsupported_caption_language: 'Teletext subtitles can show Latin, Cyrillic, Greek, Arabic and Hebrew scripts only.',
});

// Core words these better than the fallback (they name what SyncWords said).
const PREFER_CORE_MESSAGE = ['captions_provider_rejected', 'captions_unsupported_caption_language'];

export function syncwordsErrorMessage(code, coreMessage = '') {
	if (!code || !SYNCWORDS_ERROR_MESSAGES[code]) return '';
	if (PREFER_CORE_MESSAGE.includes(code) && coreMessage) return coreMessage;
	return SYNCWORDS_ERROR_MESSAGES[code];
}
